/**
 * Google Play Integrity as a signal (feature 'integrity'): is this the genuine app, installed from Google Play, on a
 * device that meets Android's integrity? Never a refusal of its own: the verdict is recorded for the player (C.integrity,
 * the account audit, a log line) and the purchase audit names it (MonetizationAudit signal: 'integrity' of a credit),
 * so an operator sees it next to a purchase and a sign-in. Enforcing it (refusing purchases or sign-ins of failing
 * devices) is a later decision of the owner (docs/integrations-setup.md).
 *
 *  - integrity.challenge: a one-time challenge of the player (memory, config.integrity.challengeTtlMs, at most 3 open).
 *  - The app asks the Play Integrity API (Standard request, android/.../FwpIntegrity.java) for a token with the
 *    challenge as its requestHash.
 *  - integrity.verify { token, challenge }: the challenge is used up; the token is decoded at Google
 *    (IntegrityVerifier, src/server/node/play-integrity.ts: playintegrity.googleapis.com decodeIntegrityToken with the
 *    service account) and read: requestHash = the challenge, packageName = the game's, timestamp within
 *    config.integrity.maxTokenAgeMs, appRecognitionVerdict PLAY_RECOGNIZED, deviceRecognitionVerdict with
 *    MEETS_DEVICE_INTEGRITY, appLicensingVerdict LICENSED. 'ok' when every check passes, 'suspicious' when the token is
 *    this challenge's but a check fails, 'failed' when it is not (wrong challenge, stale, another package).
 */
import { BackendError } from '../../../shared/protocol/errors';
import type { IntegrityChallenge, IntegrityResult, PlayerId } from '../../../shared/protocol/model';
import { randomToken } from '../../../shared/random';
import { type StorageBatchOp, deleteOp } from '../storage';
import type { AccountAudit } from './account-audit';
import { C, type ServiceDeps } from './deps';

/** The fields of a decoded Play Integrity token payload the server reads (null: absent). */
export interface IntegrityVerdict {
  requestHash: string | null;
  packageName: string | null;
  /** requestDetails.timestampMillis. */
  timestampMs: number | null;
  /** appIntegrity.appRecognitionVerdict: PLAY_RECOGNIZED, UNRECOGNIZED_VERSION, UNEVALUATED. */
  appRecognition: string | null;
  /** deviceIntegrity.deviceRecognitionVerdict: MEETS_DEVICE_INTEGRITY, MEETS_BASIC_INTEGRITY, MEETS_STRONG_INTEGRITY... */
  deviceRecognition: string[];
  /** accountDetails.appLicensingVerdict: LICENSED, UNLICENSED, UNEVALUATED. */
  appLicensing: string | null;
}

/** Decodes an integrity token at Google (src/server/node/play-integrity.ts). */
export interface IntegrityVerifier {
  readonly packageName: string;
  /** Throws IntegrityError('invalid') for a token Google refuses, IntegrityError('unavailable') when it cannot be asked. */
  decode(token: string): Promise<IntegrityVerdict>;
}

export class IntegrityError extends Error {
  constructor(
    message: string,
    readonly kind: 'invalid' | 'unavailable',
  ) {
    super(message);
    this.name = 'IntegrityError';
  }
}

/** The latest verdict of a player (C.integrity, id = playerId). */
export interface IntegrityRecord extends IntegrityResult {
  playerId: PlayerId;
}

const MAX_OPEN_CHALLENGES = 3;

/** Reads a verdict against the challenge (see the module comment). */
export function readVerdict(v: IntegrityVerdict, expected: { challenge: string; packageName: string; now: number; maxAgeMs: number }): IntegrityResult {
  const fresh = v.timestampMs !== null && Math.abs(expected.now - v.timestampMs) <= expected.maxAgeMs;
  const ours = v.requestHash === expected.challenge && v.packageName === expected.packageName;
  const checks = {
    app: v.appRecognition === 'PLAY_RECOGNIZED',
    device: v.deviceRecognition.includes('MEETS_DEVICE_INTEGRITY') || v.deviceRecognition.includes('MEETS_STRONG_INTEGRITY'),
    licensed: v.appLicensing === 'LICENSED',
    fresh,
  };
  const verdict: IntegrityResult['verdict'] = !ours || !fresh ? 'failed' : checks.app && checks.device && checks.licensed ? 'ok' : 'suspicious';
  return { verdict, checks, at: expected.now };
}

export class IntegrityService {
  /** Open challenges per player (memory). */
  private readonly challenges = new Map<PlayerId, Array<{ challenge: string; expiresAt: number }>>();
  /** The latest verdict per player this process knows (the purchase audit's signal; bounded). */
  private readonly latest = new Map<PlayerId, IntegrityResult['verdict']>();

  constructor(
    private readonly d: ServiceDeps,
    private readonly audit: AccountAudit,
    private readonly verifier: IntegrityVerifier | null,
  ) {}

  get enabled(): boolean {
    return this.verifier !== null;
  }

  /** The player's latest verdict ('none' when this process has seen none): the purchase audit's signal. */
  signalOf(playerId: PlayerId): string {
    return this.latest.get(playerId) ?? 'none';
  }

  challenge(playerId: PlayerId): IntegrityChallenge {
    const now = this.d.clock.now();
    const open = (this.challenges.get(playerId) ?? []).filter((c) => c.expiresAt > now).slice(-(MAX_OPEN_CHALLENGES - 1));
    const c = { challenge: randomToken(this.d.rng, 24), expiresAt: now + this.d.config.integrity.challengeTtlMs };
    open.push(c);
    this.challenges.set(playerId, open);
    if (this.challenges.size > 50_000) this.challenges.delete(this.challenges.keys().next().value as PlayerId);
    return { ...c };
  }

  async verify(playerId: PlayerId, token: string, challenge: string): Promise<IntegrityResult> {
    if (!this.verifier) throw new BackendError('FORBIDDEN', 'Play Integrity is not configured on this server', { reason: 'feature_disabled', feature: 'integrity' });
    const now = this.d.clock.now();
    const open = this.challenges.get(playerId) ?? [];
    const hit = open.find((c) => c.challenge === challenge && c.expiresAt > now);
    if (!hit) throw new BackendError('VALIDATION_FAILED', 'Unknown or expired integrity challenge', { reason: 'integrity_challenge_invalid' });
    this.challenges.set(playerId, open.filter((c) => c !== hit));
    let result: IntegrityResult;
    try {
      const v = await this.verifier.decode(token);
      result = readVerdict(v, { challenge, packageName: this.verifier.packageName, now: this.d.clock.now(), maxAgeMs: this.d.config.integrity.maxTokenAgeMs });
    } catch (err) {
      if (!(err instanceof Error && 'kind' in err && (err as IntegrityError).kind === 'invalid')) {
        // Google could not be asked: the challenge stays open for a retry of the same token.
        this.challenges.set(playerId, [...(this.challenges.get(playerId) ?? []), hit]);
        this.d.logger.warn('integrity token not decoded', { err: err instanceof Error ? err.message.slice(0, 200) : String(err) });
        throw new BackendError('UNAVAILABLE', 'Google could not decode the integrity token right now', { reason: 'integrity_unavailable' });
      }
      result = { verdict: 'failed', checks: { app: false, device: false, licensed: false, fresh: false }, at: this.d.clock.now() };
    }
    const rec: IntegrityRecord = { playerId, ...result };
    await this.d.storage.put(C.integrity, playerId, rec);
    this.latest.delete(playerId);
    this.latest.set(playerId, result.verdict);
    if (this.latest.size > 50_000) this.latest.delete(this.latest.keys().next().value as PlayerId);
    await this.audit.record({ event: 'integrity_verdict', playerId, ok: result.verdict === 'ok', details: { verdict: result.verdict, ...result.checks } });
    return result;
  }

  /** The player's stored verdict (account deletion). */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    this.challenges.delete(playerId);
    this.latest.delete(playerId);
    return (await this.d.storage.get(C.integrity, playerId)) ? [deleteOp(C.integrity, playerId)] : [];
  }
}
